import { useTranslation } from 'react-i18next'
import { Select } from '../common/Select'
import { Checkbox } from '../common/Checkbox'

interface ScheduleBuilderProps {
  frequency: string
  dayOfWeek?: number
  ordinal?: string
  dayOfMonth?: number
  oneTimeDate?: string
  remindDayBefore?: boolean
  onChange: (values: {
    frequency: string
    dayOfWeek?: number
    ordinal?: string
    dayOfMonth?: number
    oneTimeDate?: string
    remindDayBefore?: boolean
  }) => void
}

export function ScheduleBuilder({
  frequency,
  dayOfWeek,
  ordinal,
  dayOfMonth,
  oneTimeDate,
  remindDayBefore,
  onChange,
}: ScheduleBuilderProps) {
  const { t } = useTranslation()

  const frequencyOptions = [
    { value: 'daily', label: t('medicationManagement.schedule.daily') },
    { value: 'weekly', label: t('medicationManagement.schedule.weekly') },
    { value: 'biweekly', label: t('medicationManagement.schedule.biweekly') },
    { value: 'monthly_day_of_week', label: t('medicationManagement.schedule.monthlyDayOfWeek') },
    { value: 'monthly_date', label: t('medicationManagement.schedule.monthlyDate') },
    { value: 'one_time', label: t('medicationManagement.schedule.oneTime') },
  ]

  const dayOfWeekOptions = [
    { value: '0', label: t('medicationManagement.schedule.days.sunday') },
    { value: '1', label: t('medicationManagement.schedule.days.monday') },
    { value: '2', label: t('medicationManagement.schedule.days.tuesday') },
    { value: '3', label: t('medicationManagement.schedule.days.wednesday') },
    { value: '4', label: t('medicationManagement.schedule.days.thursday') },
    { value: '5', label: t('medicationManagement.schedule.days.friday') },
    { value: '6', label: t('medicationManagement.schedule.days.saturday') },
  ]

  const ordinalOptions = [
    { value: '1st', label: t('medicationManagement.schedule.ordinals.1st') },
    { value: '2nd', label: t('medicationManagement.schedule.ordinals.2nd') },
    { value: '3rd', label: t('medicationManagement.schedule.ordinals.3rd') },
    { value: '4th', label: t('medicationManagement.schedule.ordinals.4th') },
    { value: 'last', label: t('medicationManagement.schedule.ordinals.last') },
  ]

  const dayOfMonthOptions = Array.from({ length: 31 }, (_, i) => ({
    value: String(i + 1),
    label: String(i + 1),
  }))

  const showDayOfWeek =
    frequency === 'weekly' || frequency === 'biweekly' || frequency === 'monthly_day_of_week'
  const showOrdinal = frequency === 'monthly_day_of_week'
  const showDayOfMonth = frequency === 'monthly_date'
  const showOneTime = frequency === 'one_time'

  const containerStyles: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.75rem',
  }

  const rowStyles: React.CSSProperties = {
    display: 'flex',
    gap: '0.75rem',
    flexWrap: 'wrap',
  }

  const selectWrapperStyles: React.CSSProperties = {
    flex: 1,
    minWidth: '140px',
  }

  return (
    <div style={containerStyles}>
      <Select
        label={t('medicationManagement.schedule.frequency')}
        value={frequency}
        onChange={value => {
          const needsDay =
            value === 'weekly' || value === 'biweekly' || value === 'monthly_day_of_week'
          onChange({
            frequency: value,
            dayOfWeek: needsDay ? (dayOfWeek ?? 1) : undefined,
            ordinal: value === 'monthly_day_of_week' ? (ordinal ?? '1st') : undefined,
            dayOfMonth: value === 'monthly_date' ? (dayOfMonth ?? 1) : undefined,
            oneTimeDate: value === 'one_time' ? (oneTimeDate ?? '') : undefined,
            remindDayBefore: value === 'one_time' ? (remindDayBefore ?? false) : undefined,
          })
        }}
        options={frequencyOptions}
      />

      {(showDayOfWeek || showOrdinal || showDayOfMonth) && (
        <div style={rowStyles}>
          {showOrdinal && (
            <div style={selectWrapperStyles}>
              <Select
                label={t('medicationManagement.schedule.ordinal')}
                value={ordinal ?? '1st'}
                onChange={value => {
                  onChange({ frequency, dayOfWeek, ordinal: value, dayOfMonth })
                }}
                options={ordinalOptions}
              />
            </div>
          )}

          {showDayOfWeek && (
            <div style={selectWrapperStyles}>
              <Select
                label={t('medicationManagement.schedule.dayOfWeek')}
                value={String(dayOfWeek ?? 1)}
                onChange={value => {
                  onChange({
                    frequency,
                    dayOfWeek: parseInt(value, 10),
                    ordinal,
                    dayOfMonth,
                  })
                }}
                options={dayOfWeekOptions}
              />
            </div>
          )}

          {showDayOfMonth && (
            <div style={selectWrapperStyles}>
              <Select
                label={t('medicationManagement.schedule.dayOfMonth')}
                value={String(dayOfMonth ?? 1)}
                onChange={value => {
                  onChange({
                    frequency,
                    dayOfWeek,
                    ordinal,
                    dayOfMonth: parseInt(value, 10),
                  })
                }}
                options={dayOfMonthOptions}
              />
            </div>
          )}
        </div>
      )}

      {showOneTime && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          <div>
            <label
              style={{
                display: 'block',
                fontSize: '14px',
                fontWeight: 500,
                color: '#374151',
                marginBottom: '0.25rem',
              }}
            >
              {t('medicationManagement.schedule.oneTimeDate')}
            </label>
            <input
              type="date"
              value={oneTimeDate ?? ''}
              min={new Date().toISOString().split('T')[0]}
              onChange={e => {
                onChange({
                  frequency,
                  dayOfWeek,
                  ordinal,
                  dayOfMonth,
                  oneTimeDate: e.target.value,
                  remindDayBefore,
                })
              }}
              style={{
                width: '100%',
                padding: '0.5rem 0.75rem',
                border: '1px solid #d1d5db',
                borderRadius: '8px',
                fontSize: '16px',
                minHeight: '44px',
                fontFamily: 'inherit',
                boxSizing: 'border-box',
              }}
            />
          </div>
          <Checkbox
            label={t('medicationManagement.schedule.remindDayBefore')}
            description={t('medicationManagement.schedule.remindDayBeforeDescription')}
            checked={remindDayBefore ?? false}
            onChange={e => {
              onChange({
                frequency,
                dayOfWeek,
                ordinal,
                dayOfMonth,
                oneTimeDate,
                remindDayBefore: (e.target as HTMLInputElement).checked,
              })
            }}
          />
        </div>
      )}
    </div>
  )
}
